// Apex Business Intelligence — Round 1 & 2 (additive)
// Self-contained Admin pages: Team & Access, Add Team Member Wizard,
// Company Onboarding Wizard, Company Profile, Setup Checklist widget,
// Location Switcher, Invite Acceptance screen.
//
// Uses the existing app's CSS variables and Bearer auth token from localStorage.
// Does not import from App.js to keep the dependency graph one-way.
import { EditAccessModal } from "./team_access_edit";

import React, { useEffect, useMemo, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? {Authorization: "Bearer " + t} : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, {...opts, headers: {"Content-Type": "application/json", ...auth(), ...(opts.headers || {})}})
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), {data, status: r.status}); return data; });

// ─── Lightweight Toast ───────────────────────────────────────────────────
let _toastSetter = null;
export function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => { _toastSetter = setItems; return () => { _toastSetter = null; }; }, []);
  return (
    <div style={{position:"fixed",bottom:18,right:18,zIndex:9000,display:"flex",flexDirection:"column",gap:8}}>
      {items.map(t => (
        <div key={t.id} data-testid="iv-toast" style={{minWidth:260,background:"var(--bg2)",border:`1px solid ${t.kind==="err"?"#cc0000":"#1d4ed8"}`,padding:"10px 14px",borderRadius:6,color:"var(--text)",fontSize:12,boxShadow:"0 8px 20px rgba(0,0,0,0.25)"}}>
          <strong style={{color:t.kind==="err"?"#cc0000":"#1d4ed8"}}>{t.kind==="err"?"Error":"✓"}</strong> {t.msg}
        </div>
      ))}
    </div>
  );
}
export const toast = (msg, kind = "ok") => {
  if (!_toastSetter) return;
  const id = Math.random().toString(36).slice(2);
  _toastSetter(prev => [...prev, {id, msg, kind}]);
  setTimeout(() => _toastSetter && _toastSetter(prev => prev.filter(x => x.id !== id)), 3500);
};

// ─── Hook: debounced auto-save ───────────────────────────────────────────
export function useAutoSave(value, save, delay = 600) {
  useEffect(() => {
    if (value === undefined) return;
    const t = setTimeout(() => { try { save(value); } catch {} }, delay);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);
}

// ─── Shared styles (re-use existing CSS vars; no new theme) ──────────────
const labelStyle = {fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:"#2C2C2C",marginBottom:4,display:"block",fontWeight:600};
const inputStyle = {padding:"8px 10px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text)",fontSize:12,fontFamily:"var(--dm)",width:"100%"};
const cardStyle = {background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:18};
const sectionHeader = {fontFamily:"var(--rajdhani)",fontWeight:700,fontSize:14,color:"#2C2C2C"};
const checkboxLabel = {display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#2C2C2C"};

// ─── Setup Checklist Widget (dashboard) ──────────────────────────────────
export function SetupChecklistWidget({ onOpenWizard }) {
  const [data, setData] = useState(null);
  useEffect(() => { j("/api/iv/onboarding/state").then(setData).catch(() => {}); }, []);
  if (!data || data?.checklist?.complete) return null;
  const items = data.checklist?.items || {};
  const labels = {identity:"Company Identity",branding:"Branding",contact:"Contact & Location",licensing:"Licensing & Insurance",integrations:"Integrations",team:"Team Setup"};
  return (
    <div data-testid="setup-checklist-widget" style={{...cardStyle,marginBottom:18,borderColor:"#1d4ed8"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:15,fontWeight:700}}>Setup Checklist · {data.checklist.percent}% complete</div>
        <button data-testid="setup-resume-btn" className="btn btn-primary" style={{fontSize:11}} onClick={onOpenWizard}>Complete Remaining Items</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
        {Object.entries(items).map(([k,v]) => (
          <div key={k} style={{padding:"8px 10px",background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:4,fontSize:11,color:v?"#1d4ed8":"var(--text3)"}}>
            {v ? "✓" : "○"} {labels[k]||k}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Location Switcher (header) ──────────────────────────────────────────
export function LocationSwitcher({ value, onChange }) {
  const [locs, setLocs] = useState([]);
  useEffect(() => { j("/api/iv/locations").then(d => setLocs(d.locations || [])).catch(() => setLocs([])); }, []);
  return (
    <select data-testid="iv-location-switcher" value={value || "all"} onChange={e => onChange(e.target.value)}
      style={{padding:"6px 10px",background:"var(--bg3)",border:"1px solid var(--border)",borderRadius:4,color:"var(--text2)",fontSize:11,fontFamily:"var(--dm)",cursor:"pointer"}}>
      <option value="all">All Locations</option>
      {locs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
  );
}

// ─── Team & Access page ─────────────────────────────────────────────────
export function TeamAndAccess({ onOpenWizard }) {
  const [tab, setTab] = useState("users");
  const [editing, setEditing] = useState(null);
  const [seats, setSeats] = useState(null);
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => {
    j("/api/iv/seats").then(setSeats).catch(() => {});
    j("/api/iv/team").then(d => setTeam(d.team || [])).catch(() => {});
  }, []);
  useEffect(refresh, [refresh]);
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return team.filter(m => !q || (m.full_name||"").toLowerCase().includes(q) || (m.email||"").toLowerCase().includes(q) || (m.system_role||"").toLowerCase().includes(q));
  }, [team, search]);

  const deactivate = async (id) => {
    setBusy(true);
    try { await j(`/api/iv/team/${id}/deactivate`, {method:"POST"}); toast("Deactivated"); refresh(); }
    catch (e) { toast(e.message || "Failed", "err"); }
    finally { setBusy(false); }
  };
  const reactivate = async (id) => {
    setBusy(true);
    try { await j(`/api/iv/team/${id}/reactivate`, {method:"POST"}); toast("Reactivated"); refresh(); }
    catch (e) { toast(e.message || "Failed", "err"); }
    finally { setBusy(false); }
  };
  const resend = async (id) => {
    try { const r = await j(`/api/iv/team/${id}/resend-invite`, {method:"POST"}); toast(r.email_status?.ok ? "Invite resent" : "Invite generated (email not configured)"); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };

  return (
    <div data-testid="team-and-access-page">
      {/* Seat counter */}
      <div style={{...cardStyle,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontFamily:"var(--rajdhani)",fontSize:14,letterSpacing:1,textTransform:"uppercase",color:"var(--text3)"}}>Team Members</div>
          <div data-testid="iv-seat-counter" style={{fontFamily:"var(--rajdhani)",fontSize:28,fontWeight:700,color:"var(--text)"}}>
            {seats?.used ?? "—"} of {seats?.max ?? "∞"} used
            {seats?.remaining != null && <span style={{fontSize:12,color:"var(--text3)",marginLeft:10}}>· {seats.remaining} remaining</span>}
          </div>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>Plan: <strong style={{color:"#1d4ed8"}}>{seats?.plan || "—"}</strong> ({seats?.plan_price})</div>
          {seats?.warn && <div style={{marginTop:8,fontSize:11,color:"#cc0000"}}>⚠ You are approaching your seat limit.</div>}
          {seats?.blocked && <div style={{marginTop:8,fontSize:11,color:"#cc0000"}}>⛔ Seat limit reached — upgrade to add more team members.</div>}
        </div>
        <button data-testid="add-team-member-btn" className="btn btn-primary" disabled={seats?.blocked} onClick={onOpenWizard} style={{fontSize:12}}>+ Add Team Member</button>
      </div>

      {/* Tabs */}
      <div className="tab-row">
        <div className={`tab ${tab==="users"?"active":""}`} onClick={()=>setTab("users")} data-testid="tab-users">Users</div>
        <div className={`tab ${tab==="roles"?"active":""}`} onClick={()=>setTab("roles")} data-testid="tab-roles">Custom Roles</div>
        <div className={`tab ${tab==="audit"?"active":""}`} onClick={()=>setTab("audit")} data-testid="tab-audit">Audit Log</div>
      </div>

      {tab==="users" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Users</div>
            <input data-testid="iv-team-search" className="form-control" placeholder="Search name, email, role…" value={search} onChange={e=>setSearch(e.target.value)} style={{maxWidth:260}}/>
          </div>
          <div style={{overflowX:"auto"}}>
            <table>
              <thead><tr><th>Name</th><th>Role</th><th>Location</th><th>Status</th><th>Last Login</th><th style={{textAlign:"right"}}>Actions</th></tr></thead>
              <tbody>
                {filtered.length===0 && <tr><td colSpan={6} style={{textAlign:"center",color:"var(--text3)",padding:20}}>No team members yet. Click + Add Team Member.</td></tr>}
                {filtered.map(m => (
                  <tr key={m.id}>
                    <td><div style={{fontWeight:600,color:"var(--text)"}}>{m.full_name}</div><div style={{fontSize:11,color:"var(--text3)"}}>{m.email}</div></td>
                    <td><span className="badge badge-cyan">{m.system_role}</span></td>
                    <td>{m.location_id || "—"}</td>
                    <td>
                      {m.status==="active" && <span className="badge badge-green">Active</span>}
                      {m.status==="invited" && <span className="badge badge-amber">Invited</span>}
                      {m.status==="deactivated" && <span className="badge badge-gray">Deactivated</span>}
                    </td>
                    <td style={{fontSize:11,color:"var(--text3)"}}>{m.last_login ? new Date(m.last_login).toLocaleString() : "Never"}</td>
                    <td style={{textAlign:"right"}}>
                      {m.status==="invited" && <button className="btn btn-ghost" style={{fontSize:10,marginRight:6}} onClick={()=>resend(m.id)} data-testid={`resend-${m.id}`}>Resend Invite</button>}
                      {m.system_role!=="Owner" && <button className="btn btn-ghost" style={{fontSize:10,marginRight:6}} onClick={()=>setEditing(m)} data-testid={`edit-access-${m.id}`}>Edit access</button>}
                      <button className="btn btn-ghost" style={{fontSize:10,marginRight:6}} onClick={()=>{ if(window.confirm(`Send password reset to ${m.full_name}?`)) resend(m.id); }} data-testid={`reset-pw-${m.id}`}>Reset PW</button>
                      <button className="btn btn-ghost" style={{fontSize:10,marginRight:6}} onClick={()=>{ window.location.hash=""; setTimeout(()=>{ alert(`Activity log for ${m.full_name}\nFiltered audit query: actor_email="${m.email}"\nVisit Administration → Audit (Round 10 / Round 3) for the full log.`); },50); }} data-testid={`activity-${m.id}`}>Activity</button>
                      {m.status!=="deactivated"
                        ? <button className="btn btn-danger" disabled={busy} style={{fontSize:10}} onClick={()=>deactivate(m.id)} data-testid={`deact-${m.id}`}>Deactivate</button>
                        : <button className="btn btn-ghost" disabled={busy} style={{fontSize:10}} onClick={()=>reactivate(m.id)} data-testid={`react-${m.id}`}>Reactivate</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <EditAccessModal member={editing} onClose={()=>setEditing(null)} onSaved={refresh}/>}
      {tab==="roles" && <CustomRolesTab />}
      {tab==="audit" && <AuditLogTab />}
    </div>
  );
}

// ─── Custom Roles tab ────────────────────────────────────────────────────
function CustomRolesTab() {
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [base, setBase] = useState("Read-Only");
  const [busy, setBusy] = useState(false);
  const refresh = () => { j("/api/iv/roles/custom").then(d => setRoles(d.roles || [])).catch(() => {}); };
  useEffect(refresh, []);
  const create = async () => {
    if (!name.trim()) return toast("Role name required", "err");
    setBusy(true);
    try { await j("/api/iv/roles/custom", {method:"POST", body: JSON.stringify({name, base_role: base, permissions: {}})}); setName(""); toast("Custom role created"); refresh(); }
    catch (e) { toast(e.message || "Failed", "err"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card">
      <div className="card-header"><div className="card-title">Custom Roles (Owner-only)</div></div>
      <div className="card-body">
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <input data-testid="custom-role-name" className="form-control" placeholder="Role name (e.g. Lead Estimator)" value={name} onChange={e=>setName(e.target.value)} style={{flex:1}}/>
          <select className="form-control" value={base} onChange={e=>setBase(e.target.value)} style={{maxWidth:200}}>
            {["Admin","Estimator","Coordinator","Field Tech","Accounting","Marketing","Salesperson","Read-Only"].map(r => <option key={r} value={r}>Base: {r}</option>)}
          </select>
          <button data-testid="create-custom-role" className="btn btn-primary" onClick={create} disabled={busy} style={{fontSize:12}}>+ Create</button>
        </div>
        {roles.length===0 ? <div style={{color:"var(--text3)",fontSize:12}}>No custom roles yet. Custom roles start as a copy of the chosen base role; Owner can fine-tune permissions per role.</div>
          : <ul style={{listStyle:"none",padding:0,margin:0}}>
              {roles.map(r => <li key={r.id} style={{padding:"8px 0",borderBottom:"1px solid var(--border)",fontSize:12}}>{r.name} <span style={{color:"var(--text3)"}}>· based on {r.base_role}</span></li>)}
            </ul>}
      </div>
    </div>
  );
}

// ─── Audit Log tab ───────────────────────────────────────────────────────
function AuditLogTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (module) params.set("module", module);
    if (action) params.set("action", action);
    j(`/api/iv/audit?${params.toString()}`).then(d => setRows(d.audit || [])).catch(() => {});
  }, [q, module, action]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exportCsv = () => {
    const header = "Timestamp,User,Action,Module,Record,Field,Old,New,IP";
    const csv = [header, ...rows.map(r => [r.ts,r.actor_email,r.action,r.module,r.record_id||"",r.field||"",JSON.stringify(r.old_value||""),JSON.stringify(r.new_value||""),r.ip||""].map(v => `"${String(v).replace(/"/g,'""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], {type:"text/csv"});
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `apex-audit-${Date.now()}.csv`; a.click();
    toast("CSV downloaded");
  };
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Audit Log</div>
        <button data-testid="audit-export-csv" className="btn btn-ghost" onClick={exportCsv} style={{fontSize:11}}>Export CSV</button>
      </div>
      <div className="card-body">
        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          <input data-testid="audit-search" className="form-control" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} style={{flex:1,minWidth:160}}/>
          <select className="form-control" value={module} onChange={e=>setModule(e.target.value)} style={{maxWidth:180}}>
            <option value="">All modules</option>
            {["Jobs","Documents","Notes","Schedule","Reports","Invoices_Estimates","Payment_Financial","Administration"].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-control" value={action} onChange={e=>setAction(e.target.value)} style={{maxWidth:160}}>
            <option value="">All actions</option>
            {["create","update","delete","view","login","invite","role_change","permission_change","export","import"].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div style={{overflowX:"auto"}}>
          <table>
            <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Module</th><th>Record</th><th>Change</th></tr></thead>
            <tbody>
              {rows.length===0 && <tr><td colSpan={6} style={{textAlign:"center",color:"var(--text3)",padding:16}}>No audit events yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>{new Date(r.ts).toLocaleString()}</td>
                  <td style={{fontSize:11}}>{r.actor_email}</td>
                  <td><span className="badge badge-cyan">{r.action}</span></td>
                  <td style={{fontSize:11}}>{r.module}</td>
                  <td style={{fontSize:11,color:"var(--text3)"}}>{r.record_id || "—"}</td>
                  <td style={{fontSize:11}}>{r.field ? <><strong>{r.field}</strong>: {String(r.old_value)} → {String(r.new_value)}</> : (r.notes || "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Add Team Member Wizard (modal, 5 steps) ─────────────────────────────
const ROLE_CARDS = [
  {role:"Admin", desc:"Everything except Manage Roles, Royalties, Legacy Plan, Location Settings."},
  {role:"Estimator", desc:"Jobs, Estimates, Documents (limited), Schedule view, Contacts, Reports, Map, Clock In/Out."},
  {role:"Coordinator", desc:"Jobs edit, Schedule edit, Notes, Documents, Contacts/Companies, Map, Tag Staff, Reports, Service Requests."},
  {role:"Field Tech", desc:"Own jobs only. Clock In/Out, schedule, map, materials, notes, documents. No financial data."},
  {role:"Accounting", desc:"All financial fields, Invoices, Payments, AR Aging, Timecards approve, QuickBooks sync, Reports, Export."},
  {role:"Marketing", desc:"Jobs read-only, Contacts, Referrer data, Google Reviews, Closing % reports, Revenue by source."},
  {role:"Salesperson", desc:"Jobs create/edit, Contacts/Companies, Schedule, Estimates, Own Closing %, Notes. No financial."},
  {role:"Read-Only", desc:"View everything within their location. Cannot create, edit, delete, export, or approve."},
  {role:"Custom", desc:"Start from any base role and fine-tune permissions for this individual."},
];

export function AddTeamMemberWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [perms, setPerms] = useState({}); // override map sent to server
  const [defaults, setDefaults] = useState({}); // role default perms snapshot
  const [form, setForm] = useState({
    first_name:"", last_name:"", email:"", phone:"", job_title:"", profile_photo_b64:"",
    start_date:"", location_id:"1", employment_type:"Full-Time",
    system_role:"Read-Only",
    send_welcome_email:true, require_password_change:true, two_factor_required:false,
    notify_new_job:true, notify_schedule:true, notify_mentions:true,
    notify_sms_urgent:false, notify_daily_digest:false, notify_weekly_summary:false,
  });
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const [busy, setBusy] = useState(false);

  useEffect(() => { j("/api/iv/permissions/catalog").then(setCatalog).catch(() => {}); }, []);

  // Load role defaults when role changes
  useEffect(() => {
    if (!form.system_role || form.system_role === "Custom") return;
    j(`/api/iv/permissions/role/${encodeURIComponent(form.system_role)}`).then(d => {
      setDefaults(d.permissions || {});
      setPerms({}); // overrides reset
    }).catch(() => {});
  }, [form.system_role]);

  // Photo upload
  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5*1024*1024) return toast("Photo must be under 5MB", "err");
    const reader = new FileReader();
    reader.onload = () => set("profile_photo_b64", reader.result);
    reader.readAsDataURL(file);
  };

  const togglePerm = (key) => {
    const cur = perms[key] !== undefined ? perms[key] : defaults[key];
    const next = !cur;
    // if next equals default, drop the override
    if (next === defaults[key]) {
      setPerms(p => { const np = {...p}; delete np[key]; return np; });
    } else {
      setPerms(p => ({...p, [key]: next}));
    }
  };
  const effective = (key) => perms[key] !== undefined ? perms[key] : !!defaults[key];

  const next = () => setStep(s => Math.min(5, s+1));
  const back = () => setStep(s => Math.max(1, s-1));

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email) { toast("Name and email are required", "err"); setStep(1); return; }
    setBusy(true);
    try {
      const body = {...form, permissions_override: perms};
      const res = await j("/api/iv/team", {method:"POST", body: JSON.stringify(body)});
      toast(res.email_status?.ok ? "Team member added — invite emailed" : "Team member added (email not configured)");
      onCreated && onCreated(res.team_member);
      onClose();
    } catch (e) {
      if (e.status === 409 && e.data?.detail?.error === "seat_limit_reached") {
        toast(`Seat limit reached (${e.data.detail.max} on ${e.data.detail.plan})`, "err");
      } else {
        toast(e.message || "Failed to add team member", "err");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" data-testid="add-team-wizard">
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700}}>Add Team Member · Step {step} of 5</div>
          <button className="icon-btn" onClick={onClose} data-testid="wizard-close">✕</button>
        </div>
        {/* Progress */}
        <div style={{height:4,background:"var(--bg3)"}}><div style={{width:`${(step/5)*100}%`,height:"100%",background:"var(--cyan)"}}/></div>

        <div style={{padding:18,maxHeight:"68vh",overflowY:"auto"}}>
          {step===1 && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div><label style={labelStyle}>First Name *</label><input data-testid="tm-first" style={inputStyle} value={form.first_name} onChange={e=>set("first_name",e.target.value)}/></div>
              <div><label style={labelStyle}>Last Name *</label><input data-testid="tm-last" style={inputStyle} value={form.last_name} onChange={e=>set("last_name",e.target.value)}/></div>
              <div><label style={labelStyle}>Email (becomes login) *</label><input data-testid="tm-email" type="email" style={inputStyle} value={form.email} onChange={e=>set("email",e.target.value)}/></div>
              <div><label style={labelStyle}>Phone</label><input data-testid="tm-phone" style={inputStyle} value={form.phone} onChange={e=>set("phone",e.target.value)}/></div>
              <div><label style={labelStyle}>Job Title</label><input data-testid="tm-title" style={inputStyle} value={form.job_title} onChange={e=>set("job_title",e.target.value)}/></div>
              <div><label style={labelStyle}>Start Date</label><input data-testid="tm-start" type="date" style={inputStyle} value={form.start_date} onChange={e=>set("start_date",e.target.value)}/></div>
              <div><label style={labelStyle}>Location / Branch</label>
                <select data-testid="tm-location" style={inputStyle} value={form.location_id} onChange={e=>set("location_id",e.target.value)}>
                  {["1","2","3","4","5"].map(i => <option key={i} value={i}>Workspace {i}</option>)}
                </select></div>
              <div><label style={labelStyle}>Employment Type</label>
                <select data-testid="tm-emp-type" style={inputStyle} value={form.employment_type} onChange={e=>set("employment_type",e.target.value)}>
                  {["Full-Time","Part-Time","Contractor","Subcontractor","Seasonal"].map(t => <option key={t}>{t}</option>)}
                </select></div>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={labelStyle}>Profile Photo (optional)</label>
                <input data-testid="tm-photo" type="file" accept="image/*" onChange={onPhoto}/>
                {form.profile_photo_b64 && <img src={form.profile_photo_b64} alt="" style={{height:60,marginTop:8,borderRadius:4,border:"1px solid var(--border)"}}/>}
              </div>
            </div>
          )}

          {step===2 && (
            <div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:12}}>Select a role. You can fine-tune permissions for this individual in the next step without affecting others on the same role.</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                {ROLE_CARDS.map(c => (
                  <div key={c.role} data-testid={`role-card-${c.role.replace(/\s/g,"-")}`} onClick={()=>set("system_role", c.role==="Custom"?"Read-Only":c.role)}
                    style={{padding:12,border:`2px solid ${form.system_role===c.role?"#cc0000":"var(--border)"}`,borderRadius:8,cursor:"pointer",background:form.system_role===c.role?"rgba(204,0,0,0.06)":"var(--bg3)"}}>
                    <div style={{fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,color:form.system_role===c.role?"#cc0000":"var(--text)"}}>{c.role}</div>
                    <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step===3 && (
            <div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:12}}>Permissions for <strong>{form.system_role}</strong> · Toggle to customize for this individual.</div>
              {(catalog?.modules || []).map(m => (
                <details key={m.module} style={{border:"1px solid var(--border)",borderRadius:6,marginBottom:8,background:"var(--bg3)"}}>
                  <summary style={{padding:"10px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>{m.module.replace(/_/g," ")} <span style={{fontSize:10,color:"var(--text3)",marginLeft:6}}>({m.items.length})</span></summary>
                  <div style={{padding:"4px 12px 10px"}}>
                    {m.items.map(it => (
                      <label key={it.key} data-testid={`perm-${it.key}`} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:12,opacity: it.owner_only ? 0.5 : 1}}>
                        <input type="checkbox" disabled={it.owner_only} checked={effective(it.key)} onChange={()=>togglePerm(it.key)}/>
                        <span style={{flex:1}}>{it.label}</span>
                        {it.owner_only && <span style={{fontSize:9,color:"#cc0000",letterSpacing:1}}>OWNER ONLY</span>}
                        {it.owner_admin_default && !it.owner_only && <span style={{fontSize:9,color:"var(--text3)",letterSpacing:1}}>OWNER/ADMIN</span>}
                      </label>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}

          {step===4 && (
            <div style={{display:"grid",gap:12}}>
              <div style={{...cardStyle,background:"var(--bg3)"}}>
                <div style={{fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,marginBottom:10}}>Login Settings</div>
                {[["send_welcome_email","Send Welcome Email"],["require_password_change","Require Password Change on First Login"],["two_factor_required","Two-Factor Authentication Required"]].map(([k,l]) => (
                  <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:12}}>
                    <input type="checkbox" data-testid={`tm-${k}`} checked={!!form[k]} onChange={e=>set(k,e.target.checked)}/>{l}
                  </label>
                ))}
              </div>
              <div style={{...cardStyle,background:"var(--bg3)"}}>
                <div style={{fontFamily:"var(--rajdhani)",fontSize:14,fontWeight:700,marginBottom:10}}>Notification Preferences</div>
                {[
                  ["notify_new_job","Email for new job assignments"],
                  ["notify_schedule","Email for schedule changes"],
                  ["notify_mentions","Email for note mentions and tags"],
                  ["notify_sms_urgent","SMS / Push for urgent job updates"],
                  ["notify_daily_digest","Daily summary digest email"],
                  ["notify_weekly_summary","Weekly performance summary (Manager/Admin)"],
                ].map(([k,l]) => (
                  <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:12}}>
                    <input type="checkbox" data-testid={`tm-${k}`} checked={!!form[k]} onChange={e=>set(k,e.target.checked)}/>{l}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step===5 && (
            <div data-testid="tm-review">
              <div style={{...cardStyle,background:"var(--bg3)",display:"flex",gap:14,alignItems:"flex-start"}}>
                {form.profile_photo_b64 ? <img src={form.profile_photo_b64} alt="" style={{height:80,width:80,borderRadius:6,objectFit:"cover",border:"1px solid var(--border)"}}/>
                  : <div style={{height:80,width:80,borderRadius:6,background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,color:"var(--text3)"}}>{(form.first_name[0]||"?").toUpperCase()}{(form.last_name[0]||"").toUpperCase()}</div>}
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--rajdhani)",fontSize:20,fontWeight:700}}>{form.first_name} {form.last_name}</div>
                  <div style={{fontSize:12,color:"var(--text3)"}}>{form.job_title} · {form.email}</div>
                  <div style={{marginTop:8,fontSize:11}}>
                    <span className="badge badge-cyan">{form.system_role}</span>
                    <span style={{marginLeft:8,color:"var(--text3)"}}>Workspace {form.location_id} · {form.employment_type}</span>
                  </div>
                  <div style={{marginTop:8,fontSize:11,color:"var(--text3)"}}>
                    {Object.keys(perms).length===0 ? "Using role default permissions." : `${Object.keys(perms).length} permission override(s).`}
                  </div>
                </div>
              </div>
              <div style={{marginTop:10,fontSize:11,color:"var(--text3)"}}>An invite link valid 72 hours will be emailed to {form.email}.</div>
            </div>
          )}
        </div>

        <div style={{padding:14,borderTop:"1px solid var(--border)",background:"var(--bg3)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <button className="btn btn-ghost" onClick={onClose} data-testid="wizard-cancel" style={{fontSize:11}}>Cancel</button>
          <div style={{display:"flex",gap:8}}>
            {step>1 && <button className="btn btn-ghost" onClick={back} data-testid="wizard-back" style={{fontSize:11}}>← Back</button>}
            {step<5 && <button className="btn btn-primary" onClick={next} data-testid="wizard-next" style={{fontSize:11}}>Next →</button>}
            {step===5 && <button className="btn btn-primary" disabled={busy} onClick={submit} data-testid="wizard-submit" style={{fontSize:11}}>{busy ? "Creating…" : "Create Account & Send Invite"}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Company Onboarding Wizard (6 steps, auto-save) ─────────────────────
export function CompanyOnboarding({ onClose }) {
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { j("/api/iv/onboarding/state").then(d => { setCompany(d.company); setStep(Math.max(1, d.state?.step || 1)); setLoaded(true); }).catch(() => setLoaded(true)); }, []);

  const save = useCallback(async (patch, stepN = step, complete = false) => {
    try { const res = await j("/api/iv/onboarding/step", {method:"PUT", body: JSON.stringify({step: stepN, data: patch, set_completed: complete})}); setCompany(res.company); }
    catch (e) { toast(e.message || "Save failed", "err"); }
  }, [step]);

  // Auto-save when company state changes (debounced)
  useAutoSave(company, (c) => { if (c) save({}, step); }, 800);

  const set = (k, v) => setCompany(c => ({...(c||{}), [k]: v}));
  const setNested = (group, k, v) => setCompany(c => ({...(c||{}), [group]: {...(c?.[group]||{}), [k]: v}}));

  if (!loaded || !company) return <div className="modal-overlay"><div className="modal" style={{padding:20}}>Loading…</div></div>;

  return (
    <div className="modal-overlay" data-testid="company-onboarding-wizard">
      <style>{`[data-testid="company-onboarding-wizard"] input::placeholder, [data-testid="company-onboarding-wizard"] textarea::placeholder { color: #555555; opacity: 1; }`}</style>
      <div className="modal" style={{maxWidth:820}}>
        <div className="modal-header">
          <div style={{fontFamily:"var(--rajdhani)",fontSize:16,fontWeight:700}}>Company Setup · Step {step} of 6</div>
          <div>
            <button className="btn btn-ghost" onClick={() => { save({}, step); toast("Saved — resume any time"); onClose(); }} style={{fontSize:11,marginRight:6}} data-testid="onb-later">Complete Later</button>
            <button className="icon-btn" onClick={onClose} data-testid="onb-close">✕</button>
          </div>
        </div>
        <div style={{height:4,background:"var(--bg3)"}}><div style={{width:`${(step/6)*100}%`,height:"100%",background:"var(--cyan)"}}/></div>

        <div style={{padding:18,maxHeight:"68vh",overflowY:"auto"}}>
          {step===1 && <CompanyIdentity company={company} set={set}/>}
          {step===2 && <CompanyBranding company={company} set={set}/>}
          {step===3 && <CompanyContact company={company} set={set} setNested={setNested}/>}
          {step===4 && <CompanyLicensing company={company} set={set} setNested={setNested}/>}
          {step===5 && <CompanyIntegrations company={company} set={set} setNested={setNested}/>}
          {step===6 && <CompanyTeamStep onAdd={() => { /* triggers parent modal */ }}/>}
        </div>

        <div style={{padding:14,borderTop:"1px solid var(--border)",background:"var(--bg3)",display:"flex",justifyContent:"space-between"}}>
          <button className="btn btn-ghost" onClick={onClose} style={{fontSize:11}}>Cancel</button>
          <div style={{display:"flex",gap:8}}>
            {step>1 && <button className="btn btn-ghost" onClick={() => setStep(s => s-1)} data-testid="onb-back" style={{fontSize:11}}>← Back</button>}
            {step<6 && <button className="btn btn-primary" onClick={() => { save({}, step+1); setStep(s => s+1); }} data-testid="onb-next" style={{fontSize:11}}>Next →</button>}
            {step===6 && <button className="btn btn-primary" onClick={() => { save({}, 6, true); toast("Setup complete!"); onClose(); }} data-testid="onb-finish" style={{fontSize:11}}>Finish Setup</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyIdentity({ company, set }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Company Legal Name *</label><input data-testid="onb-legal" style={inputStyle} value={company.legal_name||""} onChange={e=>set("legal_name",e.target.value)}/></div>
      <div><label style={labelStyle}>DBA (Doing Business As)</label><input data-testid="onb-dba" style={inputStyle} value={company.dba||""} onChange={e=>set("dba",e.target.value)}/></div>
      <div><label style={labelStyle}>Company Type *</label>
        <select data-testid="onb-type" style={inputStyle} value={company.company_type||""} onChange={e=>set("company_type",e.target.value)}>
          <option value="">— select —</option>
          {["Sole Proprietorship","Partnership","LLC","S-Corp","C-Corp","Franchise","Other"].map(t => <option key={t}>{t}</option>)}
        </select></div>
      <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Industry / Service Type (multi-select)</label>
        <select multiple data-testid="onb-industries" style={{...inputStyle,minHeight:120}} value={company.industries||[]} onChange={e => set("industries", Array.from(e.target.selectedOptions).map(o=>o.value))}>
          {["Water Damage Restoration","Fire and Smoke Restoration","Mold Remediation","Storm and CAT Response","Roofing","General Contracting / Repairs","Contents Restoration","Trauma / Biohazard Cleanup","Inspections","Plumbing / HVAC / Electrical","Property Management","Insurance Adjusting","Other"].map(s => <option key={s}>{s}</option>)}
        </select></div>
      <div><label style={labelStyle}>Year Established</label><input data-testid="onb-year" type="number" style={inputStyle} value={company.year_established||""} onChange={e=>set("year_established",e.target.value)}/></div>
      <div><label style={labelStyle}>Number of Locations</label>
        <select data-testid="onb-num-loc" style={inputStyle} value={company.num_locations||"1"} onChange={e=>set("num_locations",e.target.value)}>
          {["1","2-5","6-10","11+"].map(o => <option key={o}>{o}</option>)}
        </select></div>
      <div><label style={labelStyle}>Company Website</label><input data-testid="onb-website" type="url" style={inputStyle} value={company.website||""} onChange={e=>set("website",e.target.value)}/></div>
      <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Company Tagline (140 char max)</label><input data-testid="onb-tagline" maxLength={140} style={inputStyle} value={company.tagline||""} onChange={e=>set("tagline",e.target.value)}/></div>
    </div>
  );
}

function CompanyBranding({ company, set }) {
  const onUpload = (key) => (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5*1024*1024) return toast("Max 5MB", "err");
    const reader = new FileReader(); reader.onload = () => set(key, reader.result); reader.readAsDataURL(f);
  };
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <div style={{gridColumn:"1 / -1"}}>
        <label style={labelStyle}>Company Logo (PNG/JPG/SVG, max 5MB, 400x400 recommended)</label>
        <input data-testid="onb-logo" type="file" accept="image/*" onChange={onUpload("logo_b64")}/>
        {company.logo_b64 && (
          <div style={{marginTop:8,display:"flex",gap:10,alignItems:"center"}}>
            <img src={company.logo_b64} alt="logo" style={{height:80,borderRadius:6,border:"1px solid var(--border)"}}/>
            <button className="btn btn-ghost" style={{fontSize:11}} onClick={()=>set("logo_b64","")} data-testid="onb-logo-remove">Remove</button>
          </div>)}
      </div>
      <div><label style={labelStyle}>Favicon (32x32)</label><input data-testid="onb-favicon" type="file" accept="image/*" onChange={onUpload("favicon_b64")}/></div>
      <div><label style={labelStyle}>Primary Brand Color</label><input data-testid="onb-primary" type="color" style={{...inputStyle,height:38,padding:2}} value={company.primary_color||"#cc0000"} onChange={e=>set("primary_color",e.target.value)}/></div>
      <div><label style={labelStyle}>Secondary Brand Color</label><input data-testid="onb-secondary" type="color" style={{...inputStyle,height:38,padding:2}} value={company.secondary_color||"#1d4ed8"} onChange={e=>set("secondary_color",e.target.value)}/></div>
      <div><label style={labelStyle}>Dashboard Display Name</label><input data-testid="onb-display-name" style={inputStyle} value={company.dashboard_display_name||company.legal_name||""} onChange={e=>set("dashboard_display_name",e.target.value)}/></div>
      <div style={{gridColumn:"1 / -1"}}>
        <label style={{...labelStyle,display:"flex",alignItems:"center",gap:8}}>
          <input type="checkbox" data-testid="onb-email-same" checked={!company.email_header_logo_b64} onChange={e=>set("email_header_logo_b64", e.target.checked?"":company.logo_b64||"")}/>
          Use same logo for email header
        </label>
        {company.email_header_logo_b64 && <input type="file" accept="image/*" onChange={onUpload("email_header_logo_b64")}/>}
      </div>
    </div>
  );
}

function CompanyContact({ company, set, setNested }) {
  const a = company.address || {};
  const p = company.phones || {};
  const e = company.emails || {};
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <div style={{gridColumn:"1 / -1",fontFamily:"var(--rajdhani)",fontWeight:700,fontSize:14}}>Primary Business Address</div>
      <div style={{gridColumn:"1 / -1"}}><label style={labelStyle}>Street Address *</label><input data-testid="onb-street" style={inputStyle} value={a.street||""} onChange={ev=>setNested("address","street",ev.target.value)}/></div>
      <div><label style={labelStyle}>Suite / Unit / Floor</label><input style={inputStyle} value={a.suite||""} onChange={ev=>setNested("address","suite",ev.target.value)}/></div>
      <div><label style={labelStyle}>City *</label><input data-testid="onb-city" style={inputStyle} value={a.city||""} onChange={ev=>setNested("address","city",ev.target.value)}/></div>
      <div><label style={labelStyle}>State *</label><input data-testid="onb-state" style={inputStyle} value={a.state||""} onChange={ev=>setNested("address","state",ev.target.value)}/></div>
      <div><label style={labelStyle}>ZIP *</label><input data-testid="onb-zip" style={inputStyle} value={a.zip||""} onChange={ev=>setNested("address","zip",ev.target.value)}/></div>
      <div><label style={labelStyle}>Country</label><input style={inputStyle} value={a.country||"USA"} onChange={ev=>setNested("address","country",ev.target.value)}/></div>
      <div><label style={labelStyle}>County / Region</label><input style={inputStyle} value={a.county||""} onChange={ev=>setNested("address","county",ev.target.value)}/></div>

      <div style={{gridColumn:"1 / -1",fontFamily:"var(--rajdhani)",fontWeight:700,fontSize:14,marginTop:10}}>Contact Information</div>
      <div><label style={labelStyle}>Main Business Phone *</label><input data-testid="onb-phone-main" style={inputStyle} value={p.main||""} onChange={ev=>setNested("phones","main",ev.target.value)}/></div>
      <div><label style={labelStyle}>Phone Type</label>
        <select style={inputStyle} value={p.main_type||"Office"} onChange={ev=>setNested("phones","main_type",ev.target.value)}>
          <option>Office</option><option>Mobile</option>
        </select></div>
      <div><label style={labelStyle}>Secondary Phone</label><input style={inputStyle} value={p.secondary||""} onChange={ev=>setNested("phones","secondary",ev.target.value)}/></div>
      <div><label style={labelStyle}>Fax</label><input style={inputStyle} value={p.fax||""} onChange={ev=>setNested("phones","fax",ev.target.value)}/></div>
      <div><label style={labelStyle}>Main Business Email *</label><input data-testid="onb-email-main" type="email" style={inputStyle} value={e.main||""} onChange={ev=>setNested("emails","main",ev.target.value)}/></div>
      <div><label style={labelStyle}>Billing / Accounting Email</label><input type="email" style={inputStyle} value={e.billing||""} onChange={ev=>setNested("emails","billing",ev.target.value)}/></div>
      <div><label style={labelStyle}>Dispatch / Operations Email</label><input type="email" style={inputStyle} value={e.dispatch||""} onChange={ev=>setNested("emails","dispatch",ev.target.value)}/></div>
      <div><label style={labelStyle}>After-Hours Contact</label><input style={inputStyle} placeholder="Name" value={e.after_hours_name||""} onChange={ev=>setNested("emails","after_hours_name",ev.target.value)}/></div>
      <div><label style={labelStyle}>After-Hours Phone</label><input style={inputStyle} value={e.after_hours_phone||""} onChange={ev=>setNested("emails","after_hours_phone",ev.target.value)}/></div>
    </div>
  );
}

function CompanyLicensing({ company, set, setNested }) {
  const ins = company.insurance || {};
  const licenses = company.licenses || [];
  const setLicense = (i, k, v) => set("licenses", licenses.map((l, idx) => idx===i ? {...l, [k]: v} : l));
  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{...sectionHeader}}>Licenses</div>
        <button className="btn btn-ghost" data-testid="onb-add-license" style={{fontSize:11}} onClick={()=>set("licenses", [...licenses, {number:"",state:"",expiration:""}])}>+ Add License</button>
      </div>
      {licenses.length===0 && <div style={{fontSize:12,color:"var(--text3)",marginBottom:14}}>No licenses added yet.</div>}
      {licenses.map((l, i) => (
        <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:8,marginBottom:8}}>
          <input data-testid={`onb-license-${i}-number`} style={inputStyle} placeholder="License Number" value={l.number||""} onChange={e=>setLicense(i,"number",e.target.value)}/>
          <input style={inputStyle} placeholder="State" value={l.state||""} onChange={e=>setLicense(i,"state",e.target.value)}/>
          <input style={inputStyle} type="date" value={l.expiration||""} onChange={e=>setLicense(i,"expiration",e.target.value)}/>
          <button className="btn btn-ghost" onClick={()=>set("licenses", licenses.filter((_,idx)=>idx!==i))} style={{fontSize:11}}>Remove</button>
        </div>
      ))}
      <div style={{...sectionHeader,marginTop:14}}>General Liability</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        <input style={inputStyle} data-testid="onb-gl-carrier" placeholder="Carrier" value={ins.gl_carrier||""} onChange={e=>setNested("insurance","gl_carrier",e.target.value)}/>
        <input style={inputStyle} placeholder="Policy Number" value={ins.gl_policy||""} onChange={e=>setNested("insurance","gl_policy",e.target.value)}/>
        <input style={inputStyle} type="date" value={ins.gl_expiration||""} onChange={e=>setNested("insurance","gl_expiration",e.target.value)}/>
      </div>
      <div style={{...sectionHeader}}>Workers Comp</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        <input style={inputStyle} data-testid="onb-wc-carrier" placeholder="Carrier" value={ins.wc_carrier||""} onChange={e=>setNested("insurance","wc_carrier",e.target.value)}/>
        <input style={inputStyle} placeholder="Policy Number" value={ins.wc_policy||""} onChange={e=>setNested("insurance","wc_policy",e.target.value)}/>
        <input style={inputStyle} type="date" value={ins.wc_expiration||""} onChange={e=>setNested("insurance","wc_expiration",e.target.value)}/>
      </div>
      <div style={{...sectionHeader}}>Certifications</div>
      <select multiple data-testid="onb-certs" style={{...inputStyle,minHeight:110}} value={company.certifications||[]} onChange={e => set("certifications", Array.from(e.target.selectedOptions).map(o=>o.value))}>
        {["IICRC","RIA","BBB","OSHA 10","OSHA 30","EPA Lead-Safe","Xactimate Certified","Other"].map(s => <option key={s}>{s}</option>)}
      </select>
    </div>
  );
}

function CompanyIntegrations({ company, set, setNested }) {
  const ig = company.integrations || {};
  const setIg = (k, v) => set("integrations", {...ig, [k]: v});
  const [placeStatus, setPlaceStatus] = useState(null);
  const verifyPlace = async () => {
    if (!ig.google_place_id) return toast("Enter a Place ID first", "err");
    try { const r = await j(`/api/iv/integrations/google-place/${encodeURIComponent(ig.google_place_id)}`); setPlaceStatus(r); toast(r.ok ? `Verified: ${r.result?.name}` : `Verify failed: ${r.reason}`, r.ok ? "ok" : "err"); }
    catch (e) { toast(e.message, "err"); }
  };
  const platforms = ["yelp","facebook","bbb","angi","homeadvisor","houzz","nextdoor"];
  const otherPlatforms = ["encircle","cotality_nextgear","psa_proven_jobs"];
  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>QuickBooks Online</div>
        <div style={{fontSize:12,color:"var(--text3)"}}>Status: <strong style={{color: ig.quickbooks?.connected ? "#1d4ed8" : "#cc0000"}}>{ig.quickbooks?.connected ? "Connected" : "Not Connected"}</strong></div>
        <input style={{...inputStyle,marginTop:8}} placeholder="QuickBooks Company ID" value={ig.quickbooks?.company_id||""} onChange={e=>setIg("quickbooks", {...(ig.quickbooks||{}), company_id: e.target.value})}/>
      </div>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>Estimating Platforms</div>
        {[["xactimate","Xactimate","profile_name","License Number"],["xactanalysis","XactAnalysis","carrier_program",""],["symbility","Symbility","username",""]].map(([k,name,f1,f2]) => (
          <div key={k} style={{marginBottom:10}}>
            <label style={{...checkboxLabel}}><input type="checkbox" data-testid={`onb-${k}-on`} checked={!!ig[k]?.enabled} onChange={e=>setIg(k, {...(ig[k]||{}), enabled: e.target.checked})}/> We use {name}</label>
            {ig[k]?.enabled && (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:6}}>
                <input style={inputStyle} placeholder={f1.replace(/_/g," ")} value={ig[k]?.[f1]||""} onChange={e=>setIg(k, {...(ig[k]||{}), [f1]: e.target.value})}/>
                {f2 && <input style={inputStyle} placeholder={f2} value={ig[k]?.license||""} onChange={e=>setIg(k, {...(ig[k]||{}), license: e.target.value})}/>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>Google Business Profile</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8}}>
          <input data-testid="onb-place-id" style={inputStyle} placeholder="Google Place ID" value={ig.google_place_id||""} onChange={e=>setIg("google_place_id", e.target.value)}/>
          <button className="btn btn-primary" data-testid="onb-verify-place" onClick={verifyPlace} style={{fontSize:11}}>Verify</button>
        </div>
        <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" style={{fontSize:11,color:"#1d4ed8",marginTop:6,display:"inline-block"}}>How do I find my Place ID?</a>
        {placeStatus?.ok && <div style={{marginTop:8,fontSize:11,color:"#1d4ed8"}}>✓ {placeStatus.result?.name} — {placeStatus.result?.rating}★ ({placeStatus.result?.user_ratings_total} reviews)</div>}
      </div>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>Additional Review Platforms</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {platforms.map(p => (
            <input key={p} style={inputStyle} placeholder={`${p[0].toUpperCase()+p.slice(1)} URL`} value={(ig.review_platforms||{})[p]||""} onChange={e=>setIg("review_platforms", {...(ig.review_platforms||{}), [p]: e.target.value})}/>
          ))}
        </div>
      </div>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>Communications</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <input style={inputStyle} placeholder="Google Maps API Key (optional override)" value={ig.google_maps_api_key||""} onChange={e=>setIg("google_maps_api_key", e.target.value)}/>
          <input style={inputStyle} placeholder="Business SMS Number" value={ig.sms_number||""} onChange={e=>setIg("sms_number", e.target.value)}/>
        </div>
        <select style={{...inputStyle,marginTop:8}} value={ig.email_provider||""} onChange={e=>setIg("email_provider", e.target.value)}>
          <option value="">Select email provider</option>
          <option>Gmail / Google Workspace</option><option>Microsoft Outlook 365</option><option>Other SMTP</option>
        </select>
      </div>
      <div style={{...cardStyle,background:"var(--bg3)"}}>
        <div style={{...sectionHeader,marginBottom:10}}>Other Platforms</div>
        {otherPlatforms.map(p => (
          <label key={p} style={{...checkboxLabel,marginBottom:6}}>
            <input type="checkbox" checked={!!(ig.other_platforms||{})[p]?.enabled} onChange={e=>setIg("other_platforms", {...(ig.other_platforms||{}), [p]: {...(ig.other_platforms?.[p]||{}), enabled: e.target.checked}})}/>
            {p.replace(/_/g," ")}
            {(ig.other_platforms||{})[p]?.enabled && (
              <input style={{...inputStyle,marginLeft:8,flex:1}} placeholder="Account" value={(ig.other_platforms||{})[p]?.account||""} onChange={e=>setIg("other_platforms", {...(ig.other_platforms||{}), [p]: {...(ig.other_platforms?.[p]||{}), account: e.target.value}})}/>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

function CompanyTeamStep({ onAdd }) {
  const [seats, setSeats] = useState(null);
  useEffect(() => { j("/api/iv/seats").then(setSeats).catch(() => {}); }, []);
  return (
    <div>
      <div style={{...cardStyle,background:"var(--bg3)",marginBottom:14}}>
        <div style={{...sectionHeader}}>Seats</div>
        <div style={{fontSize:22,fontWeight:700,marginTop:6}}>{seats?.used ?? "—"} of {seats?.max ?? "∞"} used</div>
        <div style={{fontSize:11,color:"var(--text3)"}}>Plan: <strong style={{color:"#1d4ed8"}}>{seats?.plan}</strong> ({seats?.plan_price})</div>
      </div>
      <div style={{fontSize:12,color:"var(--text3)"}}>
        Your owner account is auto-created and locked. To add team members, finish onboarding and use the <strong>Administration → Team and Access</strong> page. Bulk CSV invite is also available there.
      </div>
    </div>
  );
}

// ─── Company Profile page (ongoing) ───────────────────────────────────────
import { BusinessCredentialsTab, InsuranceTab } from "./r3";

export function CompanyProfile() {
  const [tab, setTab] = useState("info");
  const [company, setCompany] = useState(null);
  const [chk, setChk] = useState(null);
  useEffect(() => { j("/api/iv/company").then(d => { setCompany(d.company); setChk(d.checklist); }).catch(() => {}); }, []);
  const patch = async (p) => {
    try { const r = await j("/api/iv/company", {method:"PUT", body: JSON.stringify({patch: p})}); setCompany(r.company); setChk(r.checklist); toast("Saved"); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  if (!company) return <div style={{padding:20,color:"var(--text3)"}}>Loading company…</div>;
  const set = (k, v) => patch({[k]: v});
  const setNested = (group, k, v) => patch({[group]: {...(company[group]||{}), [k]: v}});
  return (
    <div data-testid="company-profile-page">
      <div className="tab-row">
        {[["info","Company Info"],["branding","Branding"],["locations","Locations"],["licensing","Licensing & Insurance"],["integrations","Integrations"],["team","Team"],["credentials","Business Credentials"],["insurance","Insurance"]].map(([k,l]) => (
          <div key={k} className={`tab ${tab===k?"active":""}`} onClick={()=>setTab(k)} data-testid={`cp-tab-${k}`}>{l}</div>
        ))}
      </div>
      <div className="card"><div className="card-body">
        {tab==="info" && <CompanyIdentity company={company} set={set}/>}
        {tab==="branding" && <CompanyBranding company={company} set={set}/>}
        {tab==="licensing" && <CompanyLicensing company={company} set={set} setNested={setNested}/>}
        {tab==="integrations" && <CompanyIntegrations company={company} set={set} setNested={setNested}/>}
        {tab==="locations" && <LocationsManager/>}
        {tab==="team" && <div style={{fontSize:12,color:"var(--text3)"}}>Manage team members in <strong>Administration → Team and Access</strong>.</div>}
        {tab==="credentials" && <BusinessCredentialsTab/>}
        {tab==="insurance" && <InsuranceTab/>}
      </div></div>
    </div>
  );
}

function LocationsManager() {
  const [locs, setLocs] = useState([]);
  const [draft, setDraft] = useState({name:"", phone:"", manager_email:"", address:{street:"",city:"",state:"",zip:""}});
  const refresh = () => { j("/api/iv/locations").then(d => setLocs(d.locations || [])).catch(() => {}); };
  useEffect(refresh, []);
  const add = async () => {
    if (!draft.name) return toast("Name required", "err");
    try { await j("/api/iv/locations", {method:"POST", body: JSON.stringify(draft)}); setDraft({name:"", phone:"", manager_email:"", address:{street:"",city:"",state:"",zip:""}}); refresh(); toast("Location added"); }
    catch (e) { toast(e.message || "Failed", "err"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this location?")) return; await j(`/api/iv/locations/${id}`, {method:"DELETE"}); refresh(); toast("Deleted"); };
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,marginBottom:12}}>
        <input style={inputStyle} placeholder="Location name" value={draft.name} onChange={e=>setDraft({...draft, name: e.target.value})} data-testid="loc-name"/>
        <input style={inputStyle} placeholder="Phone" value={draft.phone} onChange={e=>setDraft({...draft, phone: e.target.value})}/>
        <input style={inputStyle} placeholder="Manager email" value={draft.manager_email} onChange={e=>setDraft({...draft, manager_email: e.target.value})}/>
        <button className="btn btn-primary" onClick={add} data-testid="loc-add" style={{fontSize:11}}>+ Add</button>
      </div>
      {locs.length===0 ? <div style={{fontSize:12,color:"var(--text3)"}}>No locations yet.</div>
        : <table><thead><tr><th>Name</th><th>Phone</th><th>Manager</th><th></th></tr></thead>
          <tbody>{locs.map(l => <tr key={l.id}><td>{l.name}</td><td>{l.phone}</td><td>{l.manager_email}</td><td><button className="btn btn-ghost" style={{fontSize:10}} onClick={()=>del(l.id)}>Delete</button></td></tr>)}</tbody></table>}
    </div>
  );
}

// ─── Invite Acceptance screen ────────────────────────────────────────────
export function InviteAcceptScreen({ token, onAccepted }) {
  const [member, setMember] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { j(`/api/iv/invite/lookup/${token}`).then(d => setMember(d.member)).catch(e => setErr(e.message || "Invalid invite")); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return setErr("Password must be 8+ characters");
    if (pwd !== pwd2) return setErr("Passwords do not match");
    setBusy(true); setErr("");
    try {
      await j("/api/iv/invite/accept", {method:"POST", body: JSON.stringify({token, password: pwd})});
      toast("Account activated — signing you in");
      // Trigger login with new password
      const r = await fetch(`${API}/api/auth/login`, {method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({email: member.email, password: pwd})});
      const data = await r.json();
      if (data.token) { localStorage.setItem(TOKEN_KEY, data.token); onAccepted && onAccepted(data.user); window.location.search = ""; }
    } catch (er) { setErr(er.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg)",padding:20}}>
      <div style={{maxWidth:420,width:"100%",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:8,padding:24}}>
        <div style={{fontFamily:"var(--rajdhani)",fontSize:20,fontWeight:700,letterSpacing:1,textTransform:"uppercase",textAlign:"center",marginBottom:18}}>Accept Your Invite</div>
        {err && <div style={{padding:8,marginBottom:10,background:"rgba(204,0,0,0.1)",border:"1px solid #cc0000",borderRadius:4,fontSize:12,color:"#cc0000"}}>{err}</div>}
        {member && <div style={{fontSize:12,color:"var(--text3)",marginBottom:14,textAlign:"center"}}>Welcome <strong style={{color:"var(--text)"}}>{member.full_name}</strong> · {member.email}<br/>Set a password to activate your account.</div>}
        <form onSubmit={submit}>
          <input data-testid="invite-pwd" type="password" placeholder="Password (8+ chars)" value={pwd} onChange={e=>setPwd(e.target.value)} style={{...inputStyle,marginBottom:10}} required/>
          <input data-testid="invite-pwd2" type="password" placeholder="Confirm password" value={pwd2} onChange={e=>setPwd2(e.target.value)} style={{...inputStyle,marginBottom:14}} required/>
          <button data-testid="invite-submit" type="submit" disabled={busy || !member} className="btn btn-primary" style={{width:"100%",fontSize:13,padding:"10px 16px"}}>{busy ? "…" : "Accept & Sign In"}</button>
        </form>
      </div>
    </div>
  );
}
